import { Fragment, useState, useEffect, useRef, useCallback } from 'react';

/** Material UI Packages. */
import { Dialog, Box, FormControlLabel, Switch } from '@mui/material';
import Grid from '@mui/material/Grid2';

/** Redux Packages. */
import { useSelector, useDispatch } from 'react-redux';

/** Redux Reducers. */
import { setSnackbar } from 'shared/store/slices/snackBarSlice';

/** Deep comparison and empty value checking. */
import _ from 'lodash';

/** Internal Components. */
import { DialogButton } from 'shared/ui/Button';
import SectionBox from 'shared/ui/GridBox';
import CustomSkeleton from 'shared/ui/Skeleton';
import { PaperComponent } from 'shared/ui/DraggableDialog';
import DialogHeader from 'shared/ui/DialogHeader';
import { readSocketData, createSocketData } from 'shared/lib/fetchSocket';
import { formatDateToDateString } from 'shared/lib/dateFormatter';
import validateFields from 'shared/lib/validateFields';

import { actionMap } from './ActionMap';
import { FieldRenderer } from './FieldRenderer';
import NewShipmentDocumentDropZone from './NewShipmentDocumentDropZone';
import useDocumentApplyToForm from '../../hooks/useDocumentApplyToForm';

/** Client and Team are required for every shipment type (OIM/OEX/AIM/AEX/Trucking). */
const CLIENT_FIELD_KEY = 'il_trade_partner_id_client';
const TEAM_FIELD_KEY = 'il_team_id';

/** key → label, plus how a filled value looks: Client and Team are both { id, name } objects (OS-566). */
const REQUIRED_FIELDS = {
    [CLIENT_FIELD_KEY]: { label: 'Client', isFilled: (value) => !!value?.id },
    [TEAM_FIELD_KEY]: { label: 'Team', isFilled: (value) => !!value?.id },
};

const initValidator = {
    [CLIENT_FIELD_KEY]: false,
    [TEAM_FIELD_KEY]: false,
};

/** Initial values for autocomplete options. */
const createInitialState = () => ({
    il_trade_partner_id_client: { inputValue: '', options: [] },
    il_trade_partner_id_shipper: { inputValue: '', options: [] },
    il_trade_partner_id_consignee: { inputValue: '', options: [] },
    il_trade_partner_id_forwarder: { inputValue: '', options: [] },
    il_opus_vessel_id: { inputValue: '', options: [] },
    il_location_id_pol: { inputValue: '', options: [] },
    il_location_id_pod: { inputValue: '', options: [] },
    il_location_id_fpod: { inputValue: '', options: [] },
    il_location_id_aol: { inputValue: '', options: [] },
    il_location_id_aod: { inputValue: '', options: [] },
    il_location_id_faod: { inputValue: '', options: [] },
});

export default function NewShipmentDialog({
    newShipmentopen,
    setNewShipmentOpen,
    dialogConfig,
    hasCopy,
    setHasCopy,
    gridApiRef,
}) {
    /** Get the dispatch function from Redux to send actions to the store. */
    const dispatch = useDispatch();

    /** Value for shipment type(OIM/OEX/AIM/AEX/Trucking = 0~4). */
    const tabValue = useSelector((state) => state.tabValueForm);
    /** Get the form data for the current shipment type from. */
    const formData = useSelector((state) => state[dialogConfig.formState]);
    /** State to manage autocomplete options for form fields. */
    const [autocompleteOptions, setAutocompleteOptions] = useState(createInitialState());
    /** State to manage the loading status of the dialog. */
    const [loaded, setLoaded] = useState(false);
    /** State to manage the editing row's key in datagrid. */
    const [gridEditingKeys, setGridEditingKeys] = useState([]);
    const [tabTitles, setTabTitles] = useState({});
    /** Field-level validation flags (true = invalid / show error). */
    const [validator, setValidator] = useState(initValidator);

    /** Reference to the initial form data (used for comparison).  */
    const initialFormRef = useRef(null);
    /** Reference to a map that tracks which routes(place, date) have passed validation. */
    const routeCheckPassedMapRef = useRef(new Map());

    /** Hook to manage a loading screen until the dialog is fully rendered. */
    useEffect(() => {
        if (newShipmentopen) {
            initialFormRef.current = formData;
        }
        setLoaded(true);
    }, [newShipmentopen]);

    /** Handle to reset form data. */
    const handleClear = useCallback(() => {
        dispatch(actionMap[dialogConfig.resetAction]());
        setAutocompleteOptions(createInitialState());
        setTabTitles({});
        setValidator(initValidator);
    }, [dispatch, dialogConfig.resetAction]);

    /** Apply parsed document data to this dialog's form (DropZone callback). */
    const handleApplyParsedDataToForm = useDocumentApplyToForm(dialogConfig, setAutocompleteOptions);

    /** Handle dialog close with unsaved change check */
    const handleClose = useCallback(() => {
        const isFormChanged = !_.isEqual(formData, initialFormRef.current);
        const shouldClose =
            !isFormChanged ||
            (isFormChanged && window.confirm('Unsaved changes will be lost. Do you want to continue?'));

        if (shouldClose) {
            setNewShipmentOpen(false);
            setHasCopy(false);
            handleClear();
            routeCheckPassedMapRef.current.clear();
        }
    }, [formData, setNewShipmentOpen, setHasCopy, handleClear]);

    /** Handle to change a regular textfield component value. */
    const handleFormChange = (event) => {
        const { name, value } = event.target;
        dispatch(actionMap[dialogConfig.setFormDataAction]({ [name]: value }));
    };

    /** Handle to change a date component value. */
    const handleDateChange = (date, field) => {
        const dateValue = date.target.value;
        if (dateValue != null && dateValue.isValid()) {
            // Format as YYYY-MM-DD (date-only) since these are date fields, not datetime.
            // Calendar dates are sent as the picked year/month/day without UTC conversion (avoids off-by-one-day per timezone).
            const formattedDate = formatDateToDateString(dateValue.toDate());
            dispatch(actionMap[dialogConfig.setFormDataAction]({ [field]: formattedDate }));
        }
    };

    /** Handle to change a autocomplete component value when value should be ojbect. */
    const handleAutocompleteChange_obj = (fieldName) => (event, value) => {
        dispatch(actionMap[dialogConfig.setFormDataAction]({ [fieldName]: value ? value : '' }));
        if (REQUIRED_FIELDS[fieldName]?.isFilled(value)) {
            setValidator((prev) => ({ ...prev, [fieldName]: false }));
        }
    };

    /** Handle to change a autocomplete component value when value should be string. */
    const handleAutocompleteChange_string = (fieldName) => (event, value) => {
        dispatch(actionMap[dialogConfig.setFormDataAction]({ [fieldName]: value ? value.name : '' }));
    };

    /** Handle to change rate copy switch. */
    const handleSwitchChange = (event) => {
        const value = event.target.checked;
        dispatch(actionMap[dialogConfig.setFormDataAction]({ rate_copy: value }));
    };

    /** Request the option list for the value from the server via WebSocket. */
    const fetchAutocompleteOptions = (key, value, type) => {
        readSocketData(dispatch, tabValue, 'cell', type, key, value, (receivedData) => {
            setAutocompleteOptions((prev) => ({
                ...prev,
                [key]: { ...prev[key], options: receivedData },
            }));
        });
    };

    /** Handle to change options list using websocket. */
    const handleOptionsChange = (key, event, value, type) => {
        setAutocompleteOptions((prev) => ({
            ...prev,
            [key]: { ...prev[key], inputValue: value, options: [] },
        }));
        fetchAutocompleteOptions(key, value, type);
    };

    /** Handle to submit new data to server. */
    const handleSubmit = async () => {
        // If there are rows still in editing mode
        if (gridEditingKeys.length !== 0) {
            if (gridApiRef.current !== null) {
                // Exit edit mode for each row currently being edited
                for (const key of gridEditingKeys) {
                    await gridApiRef.current.stopRowEditMode({ id: key });
                }
            }
            // Clear the list of editing keys since editing has been stopped.
            setGridEditingKeys([]);
            dispatch(
                setSnackbar({
                    open: true,
                    message: `CNTR/ALIAS information hasn't been saved properly. Please try again.`,
                    severity: 'warning',
                })
            );
            return;
        }

        const isValid = validateFields(
            Object.fromEntries(
                Object.entries(REQUIRED_FIELDS).map(([key, rule]) => [key, () => rule.isFilled(formData[key])])
            ),
            setValidator
        );
        if (!isValid) {
            const missing = Object.entries(REQUIRED_FIELDS)
                .filter(([key, rule]) => !rule.isFilled(formData[key]))
                .map(([, rule]) => rule.label);
            dispatch(
                setSnackbar({
                    open: true,
                    message: `${missing.join(' and ')} ${missing.length > 1 ? 'are' : 'is'} required. Please select before saving.`,
                    severity: 'warning',
                })
            );
            return;
        }

        // Construct a unique route key by serializing key route-related fields from the formdata.
        const routeKey = JSON.stringify({
            pol: formData.il_location_id_pod || formData.il_location_id_aod,
            pod: formData.il_location_id_pol || formData.il_location_id_aol,
            eta: formData.eta,
            etd: formData.etd,
        });

        const emitUpdate = (routeOverride = true) => {
            return new Promise((resolve) => {
                createSocketData(
                    dispatch,
                    tabValue,
                    'row',
                    dialogConfig.tableName,
                    dialogConfig.sendReducerNames,
                    {
                        ...formData,
                        il_opus_reference_id: formData.il_opus_reference_id.toUpperCase(),
                        route: routeOverride,
                    },
                    (response) => {
                        if (response.route === true) {
                            handleClear();
                            setNewShipmentOpen(false);
                            dispatch(
                                setSnackbar({
                                    open: true,
                                    message: 'New shipment has been successfully created.',
                                    severity: 'success',
                                })
                            );
                        } else if (response.route === false) {
                            const retry = window.confirm(response.message);
                            if (retry) {
                                emitUpdate(false).then(resolve);
                            }
                            return;
                        }
                        routeCheckPassedMapRef.current.set(routeKey, true);
                    }
                );
            });
        };
        // If already validated, the route should have a value of TRUE
        const alreadyChecked = routeCheckPassedMapRef.current.get(routeKey);

        // If TRUE, request route validation from the server;
        // if FALSE, indicate that validation is not required.
        return emitUpdate(alreadyChecked ? false : true);
    };

    /** Render field based on type */
    const renderField = useCallback(
        (field) => {
            const commonProps = {
                label: field.label,
                name: field.key,
                value: formData[field.key],
                ...(field.key in REQUIRED_FIELDS ? { error: validator[field.key] } : {}),
            };

            const fieldProps = {
                field,
                commonProps,
                handleFormChange,
                handleDateChange,
                handleAutocompleteChange_obj,
                handleAutocompleteChange_string,
                handleOptionsChange,
                autocompleteOptions,
                formData,
                dialogConfig,
                setGridEditingKeys,
                gridApiRef,
                dispatch,
                tabTitles,
                setTabTitles,
            };

            const FieldComponent = FieldRenderer[field.type];
            return FieldComponent ? <FieldComponent {...fieldProps} /> : null;
        },
        [
            formData,
            validator,
            handleFormChange,
            handleDateChange,
            handleAutocompleteChange_obj,
            handleAutocompleteChange_string,
            handleOptionsChange,
            autocompleteOptions,
            dialogConfig,
            setGridEditingKeys,
            gridApiRef,
            dispatch,
            tabTitles,
            setTabTitles,
        ]
    );

    if (!loaded) return <CustomSkeleton />;

    return (
        <Dialog
            maxWidth="xl"
            fullWidth={true}
            open={newShipmentopen}
            onClose={handleClose}
            PaperProps={{
                sx: {
                    borderRadius: '18px',
                },
            }}
            PaperComponent={PaperComponent}
        >
            <Box sx={{ height: 668, display: 'flex', flexDirection: 'column' }}>
                <DialogHeader
                    cursor
                    title={dialogConfig.title}
                    onClose={handleClose}
                    rightChildren={
                        <DialogButton
                            text={'CLEAR'}
                            onClick={handleClear}
                            sx={{ width: '68px', marginRight: '9px' }}
                        />
                    }
                    onSave={handleSubmit}
                />
                {hasCopy && (
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
                        <FormControlLabel
                            control={<Switch size="small" checked={formData.rate_copy} onChange={handleSwitchChange} />}
                            label="Rate Copy"
                            sx={{
                                '& .MuiFormControlLabel-label': {
                                    fontSize: '13px',
                                    fontWeight: 'bold',
                                    color: '#00647e',
                                },
                            }}
                        />
                    </Box>
                )}
                <Grid container sx={{ ...(hasCopy ? { px: 3.5 } : { p: 3.5 }) }} spacing={2}>
                    {dialogConfig.sections.map((section) => {
                        return (
                            <SectionBox key={section.title} title={section.title} size={section.size} sx={section.sx}>
                                {section.fields.map((field, index) => (
                                    <Fragment key={field.key || `${field.type}-${index}`}>
                                        {renderField(field)}
                                    </Fragment>
                                ))}
                            </SectionBox>
                        );
                    })}
                </Grid>
                <NewShipmentDocumentDropZone
                    tabValue={tabValue}
                    onApplyParsedData={handleApplyParsedDataToForm}
                />
            </Box>
        </Dialog>
    );
}
